import classNames from 'volta/helpers/class-names';
import BvBtn from 'volta/components/bv-btn';
import BvBtnGroup from 'volta/components/bv-btn-group';
import BvIcon from 'volta/components/bv-icon';
import BvText from 'volta/components/bv-text';
import BvUnstyledBtn from 'volta/components/bv-unstyled-btn';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import { ref } from 'ember-ref-bucket';
import createRef from 'ember-ref-bucket/modifiers/create-ref';
import or from 'ember-truth-helpers/helpers/or';

import type { ContentValue } from '@glint/template';
import type { THueVariationString } from 'volta/utils/style-utils';

const DefaultAriaRoleType = 'status';

type TStatus = 'success' | 'info' | 'warning' | 'error' | 'default';

type TTypeMapping = Record<TStatus, { color: THueVariationString; defaultIcon: string }>;

const TypeMapping: TTypeMapping = {
  success: {
    color: 'green-5',
    defaultIcon: 'checkmark-circle'
  },
  info: {
    color: 'teal-5',
    defaultIcon: 'information-circle'
  },
  warning: {
    color: 'yellow-5',
    defaultIcon: 'alert-circle'
  },
  error: {
    color: 'red-5',
    defaultIcon: 'close-circle'
  },
  default: {
    color: 'gray-5',
    defaultIcon: 'information-circle'
  }
};

const Statuses = Object.keys(TypeMapping) as TStatus[];

interface BannerAction {
  content?: string;
  onClick?: () => void;
  iconLeft?: string;
  iconRight?: string;
}

interface BannerSecondaryAction {
  content?: ContentValue;
  onClick?: () => void;
  route?: string;
}

export interface BvBannerSignature {
  Args: {
    status?: TStatus;
    embedded?: boolean;
    stopAnnouncements?: boolean;
    title?: string;
    description?: string;
    action?: BannerAction;
    secondaryAction?: BannerSecondaryAction;
    onDismiss?: () => void;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

export default class BvBannerComponent extends Component<BvBannerSignature> {
  <template>
    <div
      class={{classNames
        'bv-banner'
        this.statusClass
        (if @onDismiss 'bv-banner--has-dismiss')
        (if this.showFocus 'bv-banner--key-focused')
        (if @embedded 'bv-banner--within-content-container' 'bv-banner--within-page')
      }}
      tabindex='0'
      role={{this.ariaRoleType}}
      aria-live={{if @stopAnnouncements 'off' 'polite'}}
      aria-labelledby={{this.headingID}}
      aria-describedby={{this.contentID}}
      ...attributes
      {{createRef 'wrapper' bucket=this}}
      {{on 'mouseup' this.handleMouseUp}}
      {{on 'keyup' this.handleKeyUp}}
      {{on 'blur' this.handleBlur}}
    >
      {{#if @onDismiss}}
        <div class='bv-banner__dismiss'>
          {{! template-lint-disable no-bare-strings }}
          <BvUnstyledBtn @onClick={{@onDismiss}} aria-label='Dismiss notification'>
            <BvIcon @glyph='close' />
          </BvUnstyledBtn>
        </div>
      {{/if}}
      <div class='bv-banner__ribbon'>
        <BvIcon @glyph={{this.iconName}} @color={{this.color}} @hasBackdrop={{true}} />
      </div>
      <div class='bv-banner__content-wrapper u-d-sm-flex'>
        <div class='u-flex-1'>
          {{#if @title}}
            <div class='bv-banner__heading' id={{this.headingID}}>
              <BvText @variant='heading-md' @as='p'>
                {{@title}}
              </BvText>
            </div>
          {{/if}}
          {{#if (or (has-block) @description)}}
            <div class='bv-banner__content' id={{this.contentID}}>
              {{#if (has-block)}}
                {{yield}}
              {{else}}
                {{@description}}
              {{/if}}
            </div>
          {{/if}}
        </div>
        {{#if @action}}
          <div
            class='bv-banner__actions u-flex-shrink-0 u-ml-sm-4 u-mt-2 u-mt-sm-0 u-align-self-center'
          >
            <BvBtnGroup>
              <BvBtn
                class='bv-banner__primary-action'
                @text={{@action.content}}
                @onClick={{optional @action.onClick}}
                @iconLeft={{@action.iconLeft}}
                @iconRight={{@action.iconRight}}
                @outline={{true}}
                @size={{if @embedded 'sm'}}
              />
              {{#if @secondaryAction}}
                {{#if @secondaryAction.route}}
                  <LinkTo
                    class='bv-banner__secondary-action u-unstyled-link'
                    @route={{@secondaryAction.route}}
                  >
                    <span class='bv-banner__secondary-action-text'>
                      {{@secondaryAction.content}}
                    </span>
                  </LinkTo>
                {{else}}
                  <button
                    class='bv-banner__secondary-action'
                    type='button'
                    {{on 'click' (optional @secondaryAction.onClick)}}
                  >
                    <span class='bv-banner__secondary-action-text'>
                      {{@secondaryAction.content}}
                    </span>
                  </button>
                {{/if}}
              {{/if}}
            </BvBtnGroup>
          </div>
        {{/if}}
      </div>
    </div>
  </template>
  @tracked showFocus = false;

  @ref('wrapper') wrapper: Node | null = null;
  id = `${guidFor(this)}BvBanner`;

  get headingID() {
    return `${this.id}Heading`;
  }

  get contentID() {
    return `${this.id}Content`;
  }

  get statusMapping() {
    const { status } = this.args;
    return TypeMapping[status && Statuses.includes(status) ? status : 'default'];
  }

  get ariaRoleType() {
    const { status } = this.args;
    return status && Statuses.includes(status) ? status : DefaultAriaRoleType;
  }

  get iconName() {
    return this.statusMapping.defaultIcon;
  }

  get color() {
    return this.statusMapping.color;
  }

  get statusClass() {
    const { status } = this.args;
    return status && Statuses.includes(status) ? `bv-banner--${status}` : 'bv-banner--default';
  }

  @action
  handleKeyUp(evt: KeyboardEvent) {
    if (evt.target === this.wrapper) {
      this.showFocus = true;
    }
  }

  @action
  handleBlur() {
    this.showFocus = false;
  }

  @action
  handleMouseUp(event?: MouseEvent) {
    if (!event) {
      return;
    }
    const { currentTarget } = event;
    const { showFocus } = this;
    const element = currentTarget as (EventTarget & { blur: () => void }) | null;
    if (element) {
      element.blur();
    }
    if (showFocus) {
      this.showFocus = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvBanner: typeof BvBannerComponent;
    'bv-banner': typeof BvBannerComponent;
  }
}
